import React, { useState, useEffect } from 'react';
import Table from '../../../../base/components/Table';
import PageSpinner from '../../../../base/components/PageSpinner';
import { useService } from '../../../../base/hooks/useService';
import {
  usePaginationProvider,
  useSearchProvider,
  useLocationSource
} from '../../../../base/components/Table/hooks';
import { LIMIT_OPTIONS } from '../../../../base/constants/pagination';
import NotificationsService from '../../../../services/NotificationsService';
import ToastService from '../../../../services/ToastService';
import { getColumns } from './components';
import EditNotificationModal from './components/EditNotificationModal';

export default function Settings() {
    const locationSource = useLocationSource();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({});
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const notificationsService = useService(NotificationsService);
    const toastService = useService(ToastService);

    const paginationProvider = usePaginationProvider({
        source: locationSource,
        alias: "offset",
        scope: "",
        fallback: 0
    });

    const limitProvider = usePaginationProvider({
        source: locationSource,
        alias: "limit",
        scope: "",
        fallback: 10,
        onApplyClearScope: ["offset"]
    });

    const searchProvider = useSearchProvider({
        source: locationSource,
        scope: "",
        alias: 'search',
        onApplyClearScope: ["offset"]
    });

    const offset = paginationProvider.getValue() || 0;
    const limit = limitProvider.getValue() || 10;

    // Slice data for client-side pagination
    const paginatedData = notifications.slice(offset, offset + limit);

    useEffect(() => {
        fetchNotifications();
    }, []);

    const fetchNotifications = async () => {
        try {
            setLoading(true);
            
            const response = await notificationsService.getSystemNotifications();
            setNotifications(response.templates || []);
            setPagination({ totalCount: response.templates?.length || 0 });
        } catch (err) {
            console.error('Error fetching notifications:', err);
            toastService.error('Failed to fetch notifications');
            setNotifications([]);
            setPagination({ totalCount: 0 });
        } finally {
            setLoading(false);
        }
    };

    const handleStatusToggle = async (notificationId, currentStatus) => {
        try {
            const newStatus = !currentStatus;
            await notificationsService.toggleNotificationStatus(notificationId, { isActive: newStatus });

            setNotifications(prev =>
                prev.map(notification =>
                    notification.id === notificationId
                        ? { ...notification, isActive: newStatus }
                        : notification
                )
            );

            toastService.success(`Notification ${newStatus ? 'enabled' : 'disabled'} successfully`);
        } catch (err) {
            toastService.error('Failed to update notification status');
            console.error('Error updating notification status:', err);
        }
    };

    const handleEditNotification = (notification) => {
        setSelectedNotification(notification);
        setEditModalOpen(true);
    };

    const handleEditSuccess = (updatedNotification) => {
        setNotifications(prev =>
            prev.map(notification =>
                notification.id === updatedNotification.id
                    ? updatedNotification
                    : notification
            )
        );
    };

    const handleCloseEditModal = () => {
        setEditModalOpen(false);
        setSelectedNotification(null);
    };

    return (
        <section className="notifications-settings mt-5">
            {loading ? (
                <PageSpinner />
            ) : notifications.length === 0 ? (
                <div className="text-center py-5">
                    <h5 className="text-muted">No notifications found</h5>
                    <p className="text-muted">No system notifications are currently configured.</p>
                </div>
            ) : (
                <Table
                    columns={getColumns(handleEditNotification, handleStatusToggle)}
                    data={paginatedData}
                    loading={loading}
                    totalCount={pagination.totalCount}
                    limit={limitProvider.getValue()}
                    offset={paginationProvider.getValue()}
                    paginationProvider={paginationProvider}
                    searchProvider={searchProvider}
                    isLimitEditable
                    limitProvider={limitProvider}
                    limitOptions={LIMIT_OPTIONS}
                    className="table-centered table-nowrap"
                />
            )}
            
            <EditNotificationModal
                isOpen={editModalOpen}
                onClose={handleCloseEditModal}
                notification={selectedNotification}
                onSuccess={handleEditSuccess}
            />
        </section>
    );
}
